import { createContext, useContext, useEffect, useState } from "react";
import { SearchFilters } from "../models/search";
// import { defaultFilterState } from "../constants/defaultFilterState";
import { useAtom } from "jotai";
import { areFiltersSame, getSearchBase } from "../components/filters/FilterFunctions";
import { useSearchStore } from "../store/useSearchStore";
import { FilterResetAtom } from "./FilterResetAtom";
import { useSearchId } from "./SearchIdProvider";

export type FilterContextType = {
  filterState?: SearchFilters;

  applyFilters: (newFilterState: SearchFilters, searchId?: string) => void;
  resetFilters: (resetQuery?: boolean) => void;
  hasActiveFilters: boolean;
  getDefaultSearchFilters: (resetQuery?: boolean) => SearchFilters | undefined;
};

export const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const useFilterContext = (): FilterContextType => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilters must be used within FilterProvider.");
  }
  return context;
};

export const FilterProvider = ({ children }: { children: JSX.Element }): JSX.Element => {
  const { defaultSearch } = useSearchStore();
  const { setSearchId } = useSearchId();
  const [, setFilterReset] = useAtom(FilterResetAtom);
  const FILTER_STORAGE_KEY = "appFilterState";
  const [filterState, setFilterState] = useState<SearchFilters | undefined>(() => {
    if (typeof window === "undefined") return undefined;
    try {
      const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {
      // silent, do nothing
    }
    return undefined;
  });

  const applyFilters = (newFilterState: SearchFilters, searchId?: string) => {
    if (!filterState) return;

    setSearchId(searchId || null);

    setFilterState((_filterState) => ({
      ...(_filterState ?? {}),
      ...getSearchBase(newFilterState),
    }));
  };

  const getDefaultSearchFilters = (resetQuery = false): SearchFilters | undefined =>
    defaultSearch
      ? {
          ...defaultSearch,
          /*
           * The search query and semantic checkbox value should
           * be preserved when resetting the left hand side filters.
           * Default value should fall back to the default filter state.
           * When `resetQuery` is set (e.g. navigating between top-level
           * sections) the query/semantic are reset to defaults too, so the
           * saved-search dropdown correctly shows "All Data" rather than "Custom".
           */
          query: resetQuery ? defaultSearch.query : (filterState?.query ?? defaultSearch.query),
          semantic: resetQuery ? defaultSearch.semantic : (filterState?.semantic ?? defaultSearch.semantic),
        }
      : undefined;

  const resetFilters = (resetQuery = false) => {
    const defaultSearchFilters = getDefaultSearchFilters(resetQuery);
    setFilterReset((number: number) => number + 1);
    setFilterState(defaultSearchFilters);
    setSearchId(null);
    try {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    } catch {
      // silent, do nothing
    }
  };

  const hasActiveFilters =
    filterState && defaultSearch
      ? // If filterState and defaultSearch ARE the same then there ARE NOT "active" filters
        !areFiltersSame(filterState, defaultSearch)
      : false;

  // Global filter state sync, once `defaultSearch` is set we can trigger building the `defaultSearchFilters`
  useEffect(() => {
    if (filterState) return;
    if (typeof window === "undefined") return;

    try {
      const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        setFilterState(JSON.parse(saved));
        return;
      }
    } catch {
      // silent, do nothing
    }

    if (defaultSearch) {
      const initialFilters = {
        ...defaultSearch,
        query: defaultSearch.query,
        semantic: defaultSearch.semantic,
      };
      setFilterState(initialFilters);
      try {
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(initialFilters));
      } catch {
        // handle silently
      }
    }
  }, [defaultSearch, filterState]);
  useEffect(() => {
    if (!filterState && defaultSearch) {
      resetFilters();
    }
    if (filterState) {
      try {
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterState));
      } catch {
        // handle silently
      }
    }
  }, [filterState]);

  const value: FilterContextType = {
    filterState,

    applyFilters,
    resetFilters,
    hasActiveFilters,

    getDefaultSearchFilters,
  };

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};
